/**
 * Bridge that runs inside the Unlayer editor iframe.
 *
 * CRITICAL: this function must be self-contained.
 * It is serialized via Function.prototype.toString() and injected into the iframe.
 * Do not reference imports, outer constants, or closed-over variables — only `config`.
 */
function cohoraUnlayerBridge(config) {
  if (window.__cohoraUnlayerBridgeInstalled) {
    return;
  }
  window.__cohoraUnlayerBridgeInstalled = true;

  var SOURCE = config.source;
  var APP_ORIGIN = config.appOrigin || "*";
  var REPORT_INTERVAL_MS = config.reportIntervalMs || 400;
  var HIDE_NATIVE_UPLOAD = !!config.hideNativeUpload;
  var lastPayload = "";
  var reportTimer = null;
  var rafScheduled = false;
  var messageHandler = null;
  var observer = null;

  function setNativeInputValue(input, value) {
    var proto = window.HTMLInputElement.prototype;
    var descriptor = Object.getOwnPropertyDescriptor(proto, "value");
    if (descriptor && descriptor.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
    try {
      input.dispatchEvent(new InputEvent("input", { bubbles: true, data: value }));
    } catch (e) {}
  }

  function panelRoot() {
    return (
      document.querySelector(".blockbuilder-options") ||
      document.querySelector(".blockbuilder-preferences") ||
      document.querySelector(".blockbuilder-panel") ||
      document.body
    );
  }

  function findImageUrlInputByLabel() {
    var labels = document.querySelectorAll("label, .blockbuilder-label");
    for (var i = 0; i < labels.length; i++) {
      var labelText = (labels[i].textContent || "").replace(/\s+/g, " ").trim();
      if (!/^image\s*url$/i.test(labelText)) {
        continue;
      }
      var container = labels[i].closest(".blockbuilder-widget") || labels[i].parentElement;
      if (!container) {
        continue;
      }
      var input = container.querySelector('input[type="text"], input:not([type])');
      if (input && !input.disabled) {
        return input;
      }
    }
    return null;
  }

  function findImageUrlInputFallback() {
    var root = panelRoot();
    if (!root || !/Image\s*URL/i.test(root.textContent || "")) {
      return null;
    }

    var inputs = root.querySelectorAll("input");
    var httpInput = null;
    var emptyInput = null;

    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var type = (input.type || "text").toLowerCase();
      if (input.disabled || type === "hidden" || type === "file" || type === "checkbox" || type === "radio") {
        continue;
      }
      var value = input.value || "";
      if (/^https?:\/\//i.test(value)) {
        httpInput = httpInput || input;
      } else if (!value) {
        emptyInput = emptyInput || input;
      }
    }

    return httpInput || emptyInput;
  }

  function findImageUrlInput() {
    return findImageUrlInputByLabel() || findImageUrlInputFallback();
  }

  function post(payload) {
    var message = Object.assign({ source: SOURCE }, payload);
    try {
      window.parent.postMessage(message, APP_ORIGIN);
    } catch (e) {}
    try {
      if (window.top !== window.parent) {
        window.top.postMessage(message, APP_ORIGIN);
      }
    } catch (e2) {}
  }

  function reportSelection() {
    var input = findImageUrlInput();
    var isImage = !!input;
    var currentUrl = input ? input.value || "" : "";
    var payload = (isImage ? "1" : "0") + "|" + currentUrl;
    if (payload === lastPayload) {
      return;
    }
    lastPayload = payload;
    post({ type: "SELECTION", isImage: isImage, currentUrl: currentUrl });
  }

  function hideNativeUploadButtons() {
    if (!HIDE_NATIVE_UPLOAD) {
      return;
    }
    var buttons = document.querySelectorAll("button");
    Array.prototype.forEach.call(buttons, function (btn) {
      var label = (btn.textContent || "").replace(/\s+/g, " ").trim();
      if (/^Upload Image$/i.test(label)) {
        btn.style.display = "none";
      }
    });
  }

  function scheduleWork() {
    if (rafScheduled) {
      return;
    }
    rafScheduled = true;
    requestAnimationFrame(function () {
      rafScheduled = false;
      hideNativeUploadButtons();
      reportSelection();
    });
  }

  function teardown() {
    if (reportTimer) {
      clearInterval(reportTimer);
      reportTimer = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (messageHandler) {
      window.removeEventListener("message", messageHandler);
      messageHandler = null;
    }
    window.__cohoraUnlayerBridgeInstalled = false;
  }

  messageHandler = function (event) {
    if (!event.data || event.data.source !== SOURCE) {
      return;
    }
    if (APP_ORIGIN !== "*" && event.origin !== APP_ORIGIN) {
      return;
    }

    if (event.data.type === "TEARDOWN") {
      teardown();
      return;
    }

    if (event.data.type === "SET_IMAGE_URL") {
      var input = findImageUrlInput();
      if (!input) {
        post({ type: "SET_IMAGE_URL_FAILED", reason: "Image URL input not found" });
        return;
      }

      var expected = event.data.url || "";
      setNativeInputValue(input, expected);

      setTimeout(function () {
        if ((input.value || "") === expected) {
          post({ type: "SET_IMAGE_URL_OK" });
        } else {
          post({ type: "SET_IMAGE_URL_FAILED", reason: "Image URL input did not accept value" });
        }
      }, 0);
      return;
    }

    if (event.data.type === "PING") {
      post({ type: "PONG" });
    }
  };

  window.addEventListener("message", messageHandler);

  observer = new MutationObserver(scheduleWork);
  observer.observe(document.documentElement || document.body, {
    childList: true,
    subtree: true,
  });

  hideNativeUploadButtons();
  reportSelection();
  reportTimer = setInterval(reportSelection, REPORT_INTERVAL_MS);
  post({ type: "PONG" });
}

export const buildBridgeInlineJS = (config) => `(${cohoraUnlayerBridge.toString()})(${JSON.stringify(config)});`;
